import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { SITE, NAV_LINKS } from "../data/site";
import { SERVICES } from "../data/services";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-navy/10"
            : "bg-transparent border-transparent"
        }`}
      >
        <div
          className={`max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between transition-[height] duration-500 ease-out ${
            scrolled ? "h-[84px]" : "h-[136px] lg:h-[254px]"
          }`}
        >
          <Link
            to="/"
            data-testid="header-logo-link"
            className="shrink-0"
          >
            <img
              src={SITE.logo}
              alt="Lunar Ambitions"
              className={`w-auto transition-[height] duration-500 ease-out ${
                scrolled ? "h-[68px]" : "h-[118px] lg:h-[236px]"
              }`}
            />
          </Link>

          <nav
            className="hidden md:flex items-center gap-9"
            data-testid="header-nav"
          >
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `relative text-[13px] tracking-[0.14em] uppercase font-medium transition-colors duration-300 hover:text-gold after:absolute after:left-0 after:-bottom-1.5 after:h-px after:bg-gold after:transition-all after:duration-300 ${
                    isActive
                      ? "text-navy after:w-full"
                      : "text-navy/70 after:w-0 hover:after:w-full"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={SITE.bookingGeneral}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-now-button"
              className="group inline-flex items-center gap-2 bg-navy text-white text-[13px] tracking-[0.14em] uppercase font-medium px-6 py-3 transition-colors duration-300 hover:bg-gold hover:text-navy"
            >
              Book Now
              <ArrowUpRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </nav>

          <button
            className="md:hidden text-navy p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open-button"
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-navy text-white flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu-overlay"
          >
            <div className="h-20 px-5 flex items-center justify-between">
              <img
                src={SITE.logo}
                alt="Lunar Ambitions"
                className="h-11 w-auto brightness-0 invert"
              />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="p-2"
                data-testid="mobile-menu-close-button"
              >
                <X size={28} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 pt-8 pb-10 flex flex-col gap-1">
              {[...NAV_LINKS, { label: "Home", href: "/" }].map(
                (link, i) => (
                  <motion.div
                    key={link.href + link.label}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                  >
                    <Link
                      to={link.href}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
                      className="font-display text-4xl py-3 block border-b border-white/10"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                )
              )}
              <motion.p
                className="mt-8 text-xs tracking-[0.28em] uppercase text-gold"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Treatments
              </motion.p>
              <div className="grid grid-cols-1 gap-1 mt-3">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={s.slug}
                    data-testid={`mobile-nav-service-${s.slug.slice(1)}`}
                    className="text-white/75 text-sm py-1.5 hover:text-gold transition-colors"
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
              <motion.a
                href={SITE.bookingGeneral}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-book-now-button"
                className="mt-10 inline-flex items-center justify-center gap-2 bg-gold text-navy text-sm tracking-[0.18em] uppercase font-semibold px-8 py-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
              >
                Book Now <ArrowUpRight size={16} />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
